import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  getWorkAreaAttachments,
  updateWorkAreaAttachments,
  listWorkAreas,
  WorkAreaAttachmentRule,
  WorkAreaOption,
} from '../services/WorkAreaAttachmentsService';
import { Template } from '../services/TemplatesService';

interface WorkAreaAttachmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: Template | null;
}

interface WorkAreaRow {
  work_area_id: number;
  work_area_name: string;
  linked: boolean;
  default_selected: boolean;
  order: number;
}

export function WorkAreaAttachmentsModal({
  isOpen,
  onClose,
  template,
}: WorkAreaAttachmentsModalProps) {
  const [workAreas, setWorkAreas] = useState<WorkAreaOption[]>([]);
  const [rows, setRows] = useState<WorkAreaRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && template) {
      loadData();
    }
  }, [isOpen, template]);

  const loadData = async () => {
    if (!template) return;

    setLoading(true);
    try {
      const [allWorkAreas, existingRules] = await Promise.all([
        listWorkAreas(),
        getWorkAreaAttachments(template.template_key),
      ]);

      setWorkAreas(allWorkAreas);

      const rowsData: WorkAreaRow[] = allWorkAreas.map((wa, index) => {
        const rule = existingRules.find(r => r.work_area_id === wa.id);
        return {
          work_area_id: wa.id,
          work_area_name: wa.name,
          linked: !!rule,
          default_selected: rule?.default_selected || false,
          order: rule?.order || (index + 1) * 10,
        };
      });

      setRows(rowsData);
    } catch (err: any) {
      console.error('Failed to load work area attachments:', err);
      const traceId = err?.response?.headers?.['x-trace-id'];
      toast.error(
        `Failed to load work area attachments${traceId ? ` (Trace: ${traceId})` : ''}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!template) return;

    const linkedRows = rows.filter(r => r.linked);
    const rules: WorkAreaAttachmentRule[] = linkedRows.map(r => ({
      work_area_id: r.work_area_id,
      default_selected: r.default_selected,
      order: r.order,
    }));

    setSaving(true);
    try {
      await updateWorkAreaAttachments(template.template_key, { rules });
      toast.success('Verknüpfungen gespeichert');
      onClose();
    } catch (err: any) {
      console.error('Failed to update work area attachments:', err);
      const traceId = err?.response?.headers?.['x-trace-id'];
      toast.error(
        `Failed to save work area attachments${traceId ? ` (Trace: ${traceId})` : ''}`
      );
    } finally {
      setSaving(false);
    }
  };

  const updateRow = (workAreaId: number, updates: Partial<WorkAreaRow>) => {
    setRows(prev =>
      prev.map(r =>
        r.work_area_id === workAreaId ? { ...r, ...updates } : r
      )
    );
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: 12,
          width: '90%',
          maxWidth: 800,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: 24,
            borderBottom: '1px solid #e0e3ef',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#0f2b5a' }}>
              Work Areas verknüpfen
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: 14, color: '#666' }}>
              {template?.name}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 8,
              color: '#666',
            }}
          >
            <FaTimes size={20} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
              Loading...
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fc', borderBottom: '2px solid #e0e3ef' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#666' }}>
                    Work Area
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#666' }}>
                    Verknüpft
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#666' }}>
                    Empfohlen (Default)
                  </th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#666' }}>
                    Reihenfolge
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.work_area_id}
                    style={{
                      borderBottom: '1px solid #e0e3ef',
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <td style={{ padding: '12px 16px', fontSize: 14, color: '#021a4a' }}>
                      {row.work_area_name}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={row.linked}
                        onChange={(e) => {
                          const linked = e.target.checked;
                          updateRow(row.work_area_id, {
                            linked,
                            default_selected: linked ? row.default_selected : false,
                          });
                        }}
                        style={{ cursor: 'pointer', width: 18, height: 18 }}
                      />
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={row.default_selected}
                        disabled={!row.linked}
                        onChange={(e) =>
                          updateRow(row.work_area_id, { default_selected: e.target.checked })
                        }
                        style={{
                          cursor: row.linked ? 'pointer' : 'not-allowed',
                          width: 18,
                          height: 18,
                          opacity: row.linked ? 1 : 0.4,
                        }}
                      />
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {row.linked ? (
                        <input
                          type="number"
                          value={row.order}
                          onChange={(e) =>
                            updateRow(row.work_area_id, { order: parseInt(e.target.value) || 0 })
                          }
                          style={{
                            width: 80,
                            padding: '4px 8px',
                            border: '1px solid #e0e3ef',
                            borderRadius: 4,
                            fontSize: 13,
                            textAlign: 'center',
                          }}
                        />
                      ) : (
                        <span style={{ color: '#999' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div
          style={{
            padding: 24,
            borderTop: '1px solid #e0e3ef',
            display: 'flex',
            gap: 12,
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              border: '1px solid #e0e3ef',
              borderRadius: 10,
              background: 'white',
              color: '#666',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: 10,
              background: saving || loading ? '#ccc' : '#021a4a',
              color: 'white',
              cursor: saving || loading ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
